import React from 'react';

import { defineMessages, FormattedMessage } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import { cssMap } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { OnlineDebuggerButton } from '../../online-debugger/online-debugger-button';
import { PromptOverrideButton } from '../../online-debugger/prompt-override-button';

const messages = defineMessages({
	debugger_menu_item: {
		id: 'search_ai.debugger_menu_item',
		defaultMessage: 'Debugger',
		description: 'Menu item label for the debugger button in the overflow menu',
	},
	prompt_override_menu_item: {
		id: 'search_ai.prompt_override_menu_item',
		defaultMessage: 'Prompt override',
		description: 'Menu item label for the prompt override button in the overflow menu',
	},
	store_message_label: {
		id: 'search_ai.store_message_label',
		defaultMessage: 'Record this message to help us improve answer quality',
		description: 'Label for the store message checkbox',
	},
	internal_lozenge: {
		id: 'search_ai.internal_lozenge',
		defaultMessage: 'INTERNAL',
		description: 'Label for the internal lozenge indicating this feature is for internal use only',
	},
});

const styles = cssMap({
	checkboxWrapper: {
		width: '24px',
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	dropdownContent: {
		width: '300px',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
});

const DROPDOWN_Z_INDEX = 900;

export function SAINMoreOptionsDropdown(): React.JSX.Element {
	const isArizeGateOn = fg('kd_sain_enable_arize_hello_only');
	const showOnlineDebuggerButton = fg('kd_sain_online_debugger');
	const showPromptOverrideButton = fg('convo_ai_sain_enable_prompt_override_hello_only');

	const [storeMessage, setStoreMessage] = React.useState<boolean>(() => {
		try {
			const stored = localStorage.getItem('search-ai.sain.store_message');
			return stored === null || stored === 'true';
		} catch {
			return true;
		}
	});

	const onStoreMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.stopPropagation();
		const next = e.target.checked;
		setStoreMessage(next);
		try {
			localStorage.setItem('search-ai.sain.store_message', String(next));
		} catch {
			// ignore
		}
	};

	const onMoreOptionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
	};

	return (
		<DropdownMenu
			trigger={({ triggerRef, ...props }) => (
				<IconButton
					{...props}
					ref={triggerRef}
					icon={ShowMoreHorizontalIcon}
					label="More options"
					appearance="subtle"
					spacing="compact"
					testId="sain-more-options-button"
					onClick={(e) => {
						onMoreOptionsClick(e);
						props.onClick?.(e);
					}}
				/>
			)}
			placement="bottom-end"
			zIndex={DROPDOWN_Z_INDEX}
		>
			<Box xcss={styles.dropdownContent}>
					<Stack space="space.100">
						{showOnlineDebuggerButton && (
							<Inline alignBlock="center" space="space.075">
							<OnlineDebuggerButton />
							<Text>
								<FormattedMessage {...messages.debugger_menu_item} />
							</Text>
						</Inline>
					)}
					{showPromptOverrideButton && (
						<Inline alignBlock="center" space="space.075">
							<PromptOverrideButton />
							<Text>
								<FormattedMessage {...messages.prompt_override_menu_item} />
							</Text>
						</Inline>
					)}
					{isArizeGateOn && (
						<Stack space="space.050">
							<Inline alignBlock="center" space="space.075">
								<Box xcss={styles.checkboxWrapper}>
									<Checkbox
										isChecked={storeMessage}
										onChange={onStoreMessageChange}
										testId="sain-store-message-checkbox"
									/>
								</Box>
								<Text>
									<FormattedMessage {...messages.store_message_label} />
								</Text>
							</Inline>
							<Box paddingInlineStart="space.400">
								<Lozenge appearance="default">
									<FormattedMessage {...messages.internal_lozenge} />
								</Lozenge>
							</Box>
						</Stack>
					)}
				</Stack>
			</Box>
		</DropdownMenu>
	);
}
